import _ from 'underscore';
import { useContext, useEffect, useState } from 'react';
import { useDidUpdate } from '@bingads-webui-campaign/hooks';
import { schema, deprecatedReports } from '@bingads-webui-campaign/reports-v4-schema';
import IWindow from '@bingads-webui-clientcenter/window-interface';
import { findSelectedNode } from '@bingads-webui-react/with-site-map';
import { PermissionsContext } from '@bingads-webui-react/hoc-with-permissions';
import { useReportsHistory } from '@bingads-webui-campaign/reports-v4-history';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { useReportDataSource } from '@bingads-webui-campaign/reports-v4-data-source';

import { toUi } from './converters';
import { getDefaultReportParam } from './converters/default-value';

export const getUrlParam = () => {
  if (IWindow.location.pathname.match(/\/create$/)) {
    return { reportAction: 'create', reportId: '', reportType: '' };
  }
  // TODO: another way is findSelectedNode(siteMap).apiKey, but it seems a bit strange.
  const [, reportAction, reportIdOrType] = IWindow.location.pathname.match(/\/(edit|create)\/(.*)/);
  let reportId;
  let reportType;
  switch (reportAction) {
    case 'edit':
      reportId = reportIdOrType;
      break;
    case 'create':
      reportType = reportIdOrType;
      break;
    default:
      throw new Error(`unknown reportAction ${reportAction}`);
  }
  return { reportAction, reportId, reportType };
};

export const getInitialReportParam = ({
  reportSchema, siteMap, taskItem, history, serverContext, i18n, permissions,
}) => {
  if (reportSchema) {
    const reportParam = getDefaultReportParam({
      reportSchema, siteMap, history, serverContext, i18n, permissions,
    });

    if (taskItem) {
      _.extend(reportParam, toUi({
        rawTaskItem: taskItem, entitySchema: reportSchema, permissions, history,
      }));
    }

    return reportParam;
  }

  return undefined;
};

export const getInitialReportSchema = ({
  reportAction, siteMap, taskItem,
}) => {
  if (reportAction === 'create') {
    const selectedNode = findSelectedNode(siteMap);
    const reportType = selectedNode.apiKey;
    const reportSchema = schema[reportType];

    if (!reportSchema) {
      throw new Error(`reportSchema not found for ${reportType}`);
    }

    return reportSchema;
  } else if (reportAction === 'edit') {
    return taskItem ?
      schema[taskItem.ReportType] :
      undefined;
  }

  return undefined;
};

export const useReportSchemaReportParam = ({
  siteMap,
  i18n,
  reportAction,
  reportId,
  reportType,
  retrieveSchema = getInitialReportSchema,
}) => {
  const history = useReportsHistory();
  const serverContext = useContext(ServerContext);
  const permissions = useContext(PermissionsContext);

  const [reportSchema, setReportSchema] = useState(retrieveSchema({ reportAction, siteMap, taskItem: undefined }));

  const [reportParam, setReportParam] = useState(getInitialReportParam({
    reportSchema, siteMap, history, serverContext, taskItem: undefined, i18n, permissions,
  }));

  const [reportModalParam, setReportModalParam] = useState(getInitialReportParam({
    reportSchema, siteMap, history, serverContext, taskItem: undefined, i18n, permissions,
  }));

  const [taskState, setTaskState] = useState();


  useDidUpdate(() => {
    // reset state on prop change, i.e. in main menu, click on another recent report.
    const initialReportSchema = retrieveSchema({ reportAction, siteMap, taskItem: undefined });
    const initialReportParam = getInitialReportParam({
      reportSchema: initialReportSchema, siteMap, history, serverContext, taskItem: undefined, i18n, permissions,
    });
    setReportSchema(initialReportSchema);
    setReportParam(initialReportParam);
  }, [reportAction, reportId, reportType]);

  const { getTaskItem } = useReportDataSource();

  useEffect(() => {
    if (reportAction === 'edit' && reportId) {
      getTaskItem(reportId).then((taskItem) => {
        const { ReportType } = taskItem;
        if (deprecatedReports[ReportType]) {
          setTaskState('deprecate');
          return;
        }

        setTaskState('success');
        const initialReportSchema = retrieveSchema({ reportAction, siteMap, taskItem });
        const initialReportParam = getInitialReportParam({
          reportSchema: initialReportSchema, siteMap, history, serverContext, taskItem, i18n, permissions,
        });
        setReportSchema(initialReportSchema);
        setReportParam(initialReportParam);
      }).catch((res) => {
        if (res.value && res.value[0].Code === 'InvalidId') {
          setTaskState('invalidId');
          return;
        }

        setTaskState('fail');
      });
    }
    if (reportAction === 'create') {
      setTaskState('success');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportAction, reportId, reportType]);


  return {
    taskState,
    reportSchema,
    reportParam,
    setReportParam,
    reportModalParam,
    setReportModalParam,
  };
};
